import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * Shows the discount bar and savings summary inside the Essential Cart Drawer app.
 *
 * The app renders its own panel with React, outside the theme, so the theme's cart morph never
 * reaches it. The content still comes from Shopify: every cart change refetches a section rendered
 * from the real cart, and this file only places the result. Nothing here works out an amount.
 * If the app's markup changes and the anchors are gone, nothing is inserted and the cart keeps
 * working without the bar.
 */
const SECTION_ID = 'cart-discount-rewards';
const PORTAL_SELECTOR = '#essential-cart-drawer-portal';
const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?$/;

/** @type {Record<string, string> | null} */
let fragments = null;
let latestRequest = 0;
let refreshTimer = 0;
/** @type {Map<string, HTMLElement>} */
const hosts = new Map();
/** @type {Map<string, string>} */
const renderedHtml = new Map();

function getHost(part) {
  let host = hosts.get(part);
  if (!host) {
    host = document.createElement('div');
    host.className = `cart-rewards-host cart-rewards-host--${part}`;
    host.dataset.cartRewards = part;
    hosts.set(part, host);
  }
  return host;
}

/** Finds the app's header and subtotal row. Returns null when the drawer is closed or has changed. */
function findAnchors() {
  const drawer = document.querySelector(`${PORTAL_SELECTOR} #cart-drawer`);
  const footer = drawer?.querySelector('footer');
  const header = footer?.parentElement?.parentElement?.querySelector(':scope > header');
  const payRow = footer?.querySelector('form')?.closest('footer > div');
  if (!header || !payRow) return null;

  let subtotalRow = payRow.previousElementSibling;
  while (subtotalRow instanceof HTMLElement && subtotalRow.dataset.cartRewards) {
    subtotalRow = subtotalRow.previousElementSibling;
  }
  return subtotalRow ? { header, subtotalRow } : null;
}

function render() {
  if (!fragments) return;
  const anchors = findAnchors();
  if (!anchors) return;

  const bar = getHost('bar');
  const breakdown = getHost('breakdown');
  const saved = getHost('saved');
  // Only touch the DOM when something is out of place: this runs from a MutationObserver.
  if (anchors.header.nextElementSibling !== bar) anchors.header.after(bar);
  if (anchors.subtotalRow.previousElementSibling !== breakdown) anchors.subtotalRow.before(breakdown);
  if (anchors.subtotalRow.nextElementSibling !== saved) anchors.subtotalRow.after(saved);

  for (const [part, host] of hosts) {
    const html = fragments[part] ?? '';
    if (renderedHtml.get(part) === html) continue;
    renderedHtml.set(part, html);
    // Morphing keeps the same nodes, which is what lets the bar and the circles animate.
    const next = document.createElement('div');
    next.innerHTML = html;
    morph(host, next, { ...MORPH_OPTIONS, childrenOnly: true });
  }
}

async function refresh() {
  const request = ++latestRequest;
  const root = window.Shopify?.routes?.root ?? '/';
  try {
    const response = await fetch(`${root}?sections=${SECTION_ID}`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const sections = await response.json();
    if (request !== latestRequest) return;

    const parsed = new DOMParser().parseFromString(sections[SECTION_ID] ?? '', 'text/html');
    fragments = {};
    for (const part of parsed.querySelectorAll('[data-rewards-part]')) {
      fragments[part.dataset.rewardsPart] = part.innerHTML.trim();
    }
    render();
  } catch (error) {
    console.warn('[cart-discount-bar] refresh failed', error);
  }
}

function scheduleRefresh() {
  clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(refresh, 80);
}

/**
 * The app has fixed English labels for screen readers and hidden price text, with no setting to
 * change them. They are replaced in place. A replaced label no longer matches, so it can't loop.
 */
const SPANISH_LABELS = [
  [/^Close cart$/, 'Cerrar carrito'],
  [/^Empty cart image$/, 'Carrito vacío'],
  [/^Previous products$/, 'Productos anteriores'],
  [/^Next products$/, 'Productos siguientes'],
  [/^Product recommendations$/, 'Recomendaciones de productos'],
  [/^Cart summary$/, 'Resumen del carrito'],
  [/^Item actions$/, 'Acciones del artículo'],
  [/^Announcement$/, 'Anuncio'],
  [/^Masked icon$/, 'Icono de protección'],
  [/^Payment icon (\d+)$/, 'Icono de pago $1'],
  [/^Shopping cart with (\d+) items?$/, 'Carrito con $1 artículos'],
  [/^Select variant for (.+)$/, 'Seleccionar variante de $1'],
  [/^Add (.+) to cart$/, 'Añadir $1 al carrito'],
  [/^Remove (.+) from cart$/, 'Eliminar $1 del carrito'],
  [/^Decrease quantity of (.+)$/, 'Reducir la cantidad de $1'],
  [/^Increase quantity of (.+)$/, 'Aumentar la cantidad de $1'],
  [/^(.+), quantity (\d+)$/, '$1, cantidad $2'],
];
const SPANISH_TEXT = [
  [/^Regular price:/, 'Precio habitual:'],
  [/^Sale price:/, 'Precio de oferta:'],
];

function translated(value, rules) {
  for (const [pattern, replacement] of rules) {
    if (pattern.test(value)) return value.replace(pattern, replacement);
  }
  return null;
}

function translateTextNode(node) {
  const next = translated(node.nodeValue, SPANISH_TEXT);
  if (next !== null) node.nodeValue = next;
}

function translatePortal(portal) {
  for (const element of portal.querySelectorAll('[aria-label], img[alt]')) {
    for (const attribute of ['aria-label', 'alt']) {
      const value = element.getAttribute(attribute);
      if (!value) continue;
      const next = translated(value, SPANISH_LABELS);
      if (next !== null) element.setAttribute(attribute, next);
    }
  }
  const walker = document.createTreeWalker(portal, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) translateTextNode(walker.currentNode);
}

function watchPortalLanguage(portal) {
  let frame = 0;
  new MutationObserver((mutations) => {
    // The app's countdown changes text every second, so plain text changes are handled one by one.
    const structural = mutations.some((mutation) => mutation.type !== 'characterData');
    for (const mutation of mutations) {
      if (mutation.type === 'characterData') translateTextNode(mutation.target);
    }
    if (structural && !frame) {
      frame = requestAnimationFrame(() => {
        frame = 0;
        translatePortal(portal);
      });
    }
  }).observe(portal, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['aria-label', 'alt'] });
  translatePortal(portal);
}

function watchPortal(portal) {
  watchPortalLanguage(portal);
  new MutationObserver(() => {
    const drawerOpen = findAnchors() !== null;
    if (drawerOpen && !fragments) scheduleRefresh();
    else render();
  }).observe(portal, { childList: true, subtree: true });
  if (findAnchors()) scheduleRefresh();
}

// The app and the theme both change the cart through these endpoints, so this catches every path.
new PerformanceObserver((list) => {
  const changed = list.getEntries().some((entry) => {
    try {
      return CART_MUTATION.test(new URL(entry.name).pathname);
    } catch {
      return false;
    }
  });
  if (changed) scheduleRefresh();
}).observe({ type: 'resource', buffered: false });

window.addEventListener('pageshow', (event) => {
  if (event.persisted) scheduleRefresh();
});

const existingPortal = document.querySelector(PORTAL_SELECTOR);
if (existingPortal) {
  watchPortal(existingPortal);
} else {
  const bodyObserver = new MutationObserver(() => {
    const portal = document.querySelector(PORTAL_SELECTOR);
    if (!portal) return;
    bodyObserver.disconnect();
    watchPortal(portal);
  });
  bodyObserver.observe(document.body, { childList: true });
}
